import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadOrderByOrderNumber from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrderByOrderNumber";
import { getOrderState, OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Img from "@insite/mobius/Img";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";

import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import WORAddressInfoDisplay from "@tKgpco/Components/WOR/WORAddressInfoDisplay";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";

import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {};

const statusColorMapping: Record<string, string> = {
    shipped: "#ddffdd",
    backorder: "#7f9add",
    "partially shipped": "#ddeebb",
    active: "#f2da90",
    confirmed: "#f2da90",
    picked: "#fafac8",
    delivered: "#3C7741",
    invoiced: "#3C7741",
    canceled: "#ff0000",
};
export interface OrderDetailsPrintStyles {
    container?: InjectableCss;
}

export const headerStyles: OrderDetailsPrintStyles = {
    container: {
        css: css`
            & {
                display: none;
            }

            @media print {
                main > * {
                    display: none;
                }

                main #print-div {
                    display: block;
                }

                & {
                    display: block;
                }

                table {
                    border-collapse: collapse;
                    width: 100%;
                }

                p {
                    font-size: 14px;
                }

                .info-table {
                    border-spacing: 10px;
                }

                .address-container,
                .account-info-container {
                    padding: 5px 10px;
                }

                .account-info-container {
                    padding-bottom: 0;
                    strong {
                        width: 150px;
                        margin-right: 10px;
                    }
                }
                .info-table {
                    width: 100%;
                }

                .info-table td.spacer {
                    padding-left: 15px !important;
                    padding-right: 15px !important;
                }

                .accountsumcell {
                    padding-right: 15px;
                }

                .print-custom-view .info-title {
                    background-color: #edf4f8;
                    font-weight: bold;
                    width: 100%;
                    padding: 10px;
                    font-size: 15px;
                    border-radius: 3px;
                }

                .products-table {
                    border: 1px soild;
                    margin-top: 30px;
                    table-layout: fixed;

                    th {
                        text-align: center;
                        background-color: #edf4f8;
                        border-bottom: 1px solid #fff;
                        padding: 5px;
                    }
                    td {
                        padding: 5px;
                        border: 1px solid #fff;
                        text-align: center;
                        white-space: normal;
                        word-wrap: break-word;
                    }

                    .totalCell {
                        background-color: #edf4f8;
                    }

                    .totalTd {
                        text-align: right;
                    }
                }
            }
        `,
    },
};

const mapDispatchToProps = {};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = headerStyles;
const OrderDetailsPrint: React.FunctionComponent = () => {
    const { value: order } = useContext(OrderStateContext);

    if (!order) {
        return null;
    }
    const orderShipAddress =
        !order.stCompanyName &&
        !order.stAddress1 &&
        !order.stAddress2 &&
        !order.shipToCity &&
        !order.shipToState &&
        !order.shipToState &&
        !order.shipToPostalCode &&
        !order.stCountry;

    let shipViaDescription = "No Service on Record";
    if (order.shipViaDescription || order.shipCode) {
        shipViaDescription = order.shipViaDescription || order.shipCode;
    }

    const hasShippingAddress = order.fulfillmentMethod === FulfillmentMethod.Ship || !order.fulfillmentMethod;
    return (
        <StyledWrapper id="print-div" {...styles.container}>
            <StyledWrapper className="print-custom-view">
                <table className="info-table">
                    <tr>
                        <td valign="top" className="accountsumcell">
                            <p className="info-title">{translate("Order Summary")}</p>
                            <p className="account-info-container">
                                <strong>{translate("Account #")}:</strong>
                                {order.customerNumber}
                            </p>
                            <p className="account-info-container">
                                <strong>{translate("Order #")}:</strong>
                                {order.erpOrderNumber || order.webOrderNumber}
                            </p>
                            <p className="account-info-container">
                                <strong>{translate("PO #")}:</strong>
                                {order.customerPO}
                            </p>
                            <p className="account-info-container">
                                <strong>{translate("Requested Ship Via")}:</strong>
                                {shipViaDescription}
                            </p>
                            <p className="account-info-container">
                                <strong>{translate("Purchase Order Date")}:</strong>
                                <LocalizedDateTime
                                    dateTime={order.orderDate}
                                    options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                />
                            </p>
                            <p className="account-info-container">
                                <strong>{translate("Order Status")}:</strong>
                                {order.statusDisplay}
                            </p>
                            <p className="account-info-container">
                                <strong>{translate("Order Total")}:</strong>
                                {order.orderTotalDisplay}
                            </p>
                        </td>
                        <td valign="top">
                            <p className="info-title">{translate("Billing Address")}</p>
                            <p className="address-container">
                                <AddressInfoDisplay
                                    companyName={order.btCompanyName}
                                    address1={order.btAddress1}
                                    address2={order.btAddress2}
                                    address3={order.btAddress3}
                                    address4={order.btAddress4}
                                    city={order.billToCity}
                                    state={order.billToState}
                                    postalCode={order.billToPostalCode}
                                    country={order.btCountry}
                                />
                            </p>
                        </td>
                        <td className="spacer" valign="top">
                            <p className="info-title">
                                {translate(hasShippingAddress ? "Shipping Address" : "Pick Up Location")}
                            </p>
                            <p className="address-container">
                                {orderShipAddress === true ? (
                                    <Typography>{translate("-")}</Typography>
                                ) : (
                                    <WORAddressInfoDisplay
                                        companyName={order.stCompanyName}
                                        address1={order.stAddress1}
                                        address2={order.stAddress2}
                                        address3=""
                                        address4=""
                                        city={order.shipToCity}
                                        state={order.shipToState}
                                        postalCode={order.shipToPostalCode}
                                        country={order.stCountry}
                                        shipAddress3={order.stAddress3}
                                        shipAddress4={order.stAddress4}
                                    />
                                )}
                            </p>
                        </td>
                    </tr>
                </table>

                <table className="products-table">
                    <thead>
                        <tr>
                            <th>{translate("Status")}</th>
                            <th>{translate("Line #")}</th>
                            <th>{translate("Item #")}</th>
                            <th>{translate("Cust Part #")}</th>
                            <th>{translate("MFG #")}</th>
                            <th>{translate("Description")}</th>
                            <th>{translate("UOM")}</th>
                            <th>{translate("Order Qty")}</th>
                            <th>{translate("Reserve Qty")}</th>
                            <th>{translate("Release Qty")}</th>
                            <th>{translate("Ship Date")}</th>
                            <th>{translate("RAS Date")}</th>
                            <th>{translate("Price")}</th>
                            <th>{translate("Extended Price")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {order.orderLines?.map(orderLine => {
                            return (
                                <tr key={orderLine.id}>
                                    <td
                                        style={{
                                            backgroundColor: statusColorMapping[orderLine.status.toLowerCase()]
                                                ? `${statusColorMapping[orderLine.status.toLowerCase()]}`
                                                : `${getColor("primary")}`,
                                            color: "#fff",
                                        }}
                                    >
                                        {orderLine.status === "Backorder" ? "Processing" : orderLine.status}
                                    </td>
                                    <td>{orderLine.lineNumber}</td>
                                    <td>{orderLine.productErpNumber}</td>
                                    <td>{orderLine.properties.kgpEndCustomerItemNo}</td>
                                    <td>{orderLine.manufacturerItem}</td>
                                    <td>{orderLine.shortDescription || orderLine.description}</td>
                                    <td>{orderLine.unitOfMeasure}</td>
                                    <td>{orderLine.qtyOrdered}</td>
                                    <td>{orderLine.properties.reservedQty}</td>
                                    <td>{orderLine.properties.releasedQty}</td>
                                    <td>{orderLine?.properties?.kgpCalculatedShipDate?.split(" ")[0]}</td>
                                    <td>
                                        <LocalizedDateTime
                                            dateTime={orderLine.requiredDate}
                                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                        />
                                    </td>
                                    <td>{orderLine.unitNetPrice}</td>
                                    <td>{orderLine.extendedUnitNetPrice}</td>
                                </tr>
                            );
                        })}
                        <tr>
                            <td colSpan={13} className="totalCell totalTd">
                                <strong>{translate("Order Total")}</strong>
                            </td>
                            <td colSpan={1} className="totalCell">
                                {order.orderTotalDisplay}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderDetailsPrint),
    definition: {
        displayName: "Order History Details Print",
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
